/**
 * Ordenamiento genérico de listas para tablas: compara números como números,
 * texto en español sin distinguir mayúsculas ni tildes, y deja los valores
 * vacíos siempre al final.
 */

/** Indica si un valor cuenta como vacío al ordenar. */
const isEmpty = (v) => v === null || v === undefined || v === ''

/**
 * Ordena una lista según el valor que devuelve `accessor` para cada elemento.
 *
 * @template T
 * @param {T[]} items
 * @param {(item: T) => (number|string|null|undefined)} accessor
 * @param {'asc'|'desc'} [dir='asc']
 * @returns {T[]} Copia ordenada (no muta `items`).
 */
export const sortBy = (items = [], accessor, dir = 'asc') => {
  const factor = dir === 'desc' ? -1 : 1

  return items
    .map((item) => ({ item, v: accessor(item) }))
    .sort((a, b) => {
      if (isEmpty(a.v) && isEmpty(b.v)) return 0
      if (isEmpty(a.v)) return 1
      if (isEmpty(b.v)) return -1
      const cmp =
        typeof a.v === 'number' && typeof b.v === 'number'
          ? a.v - b.v
          : String(a.v).localeCompare(String(b.v), 'es', {
              sensitivity: 'base',
              numeric: true,
            })
      return cmp * factor
    })
    .map(({ item }) => item)
}

/**
 * Siguiente orden al pulsar un encabezado: otra columna empieza ascendente;
 * la misma columna alterna asc ↔ desc.
 * @param {{key: string, dir: string}|null} current
 * @param {string} key
 * @returns {{key: string, dir: 'asc'|'desc'}}
 */
export const nextSort = (current, key) =>
  current?.key === key
    ? { key, dir: current.dir === 'asc' ? 'desc' : 'asc' }
    : { key, dir: 'asc' }
